import { useState, useEffect, useRef, useMemo } from 'react';
import gun from '../config/gun.config';
import { StabeonUser, DEFAULT_AVATAR } from '../types/user';

const ONLINE_TIMEOUT_MS = 10 * 60 * 1000; // 10 min — very forgiving

function parseAvatar(raw: any): any {
  if (!raw) return DEFAULT_AVATAR;
  if (typeof raw === 'object' && raw.emoji) return raw;
  try {
    const parsed = JSON.parse(raw);
    return parsed?.emoji ? parsed : DEFAULT_AVATAR;
  } catch { return DEFAULT_AVATAR; }
}

// interests + badges are stored as JSON strings in GUN
function parseStringArray(raw: any): string[] {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string' && raw.startsWith('[')) {
    try { const p = JSON.parse(raw); return Array.isArray(p) ? p : []; } catch { return []; }
  }
  return [];
}

export function useUsers(): StabeonUser[] {
  const [users, setUsers] = useState<Record<string, StabeonUser>>({});
  const pendingRef = useRef<Record<string, StabeonUser>>({});
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sigRef   = useRef<Record<string, string>>({});

  useEffect(() => {
    const node = gun.get('stabeon_users');
    const mapNode = node.map();

    mapNode.on((data: any, id: string) => {
      if (!data || !id || typeof data !== 'object') return;
      if (id === '_' || id.startsWith('#') || id.startsWith('~')) return;
      if (!data.nickname && !data.lastSeen) return;

      // Skip re-emissions that only changed lastSeen within the same online-window bucket (30s resolution)
      const lastSeenBucket = Math.floor((data.lastSeen || 0) / 30000);
      // Include whether a photo is present so photo changes are never skipped
      const hasPhoto = typeof data.profilePhoto === 'string' && data.profilePhoto.startsWith('data:') ? '1' : '0';
      const sig = `${data.nickname}|${data.activityCategory}|${data.activity}|${data.lat}|${data.lng}|${data.level}|${lastSeenBucket}|${hasPhoto}|${data.bio || ''}|${data.city || ''}|${data.country || ''}|${data.lookingFor || ''}|${data.interests || ''}`;
      if (sigRef.current[id] === sig) return;
      sigRef.current[id] = sig;

      const interests = parseStringArray(data.interests);
      const baseAvatar = parseAvatar(data.avatar);
      const safeProfilePhoto = typeof data.profilePhoto === 'string' && data.profilePhoto.startsWith('data:')
        ? data.profilePhoto : undefined;
      const user: StabeonUser = {
        ...data,
        nullifierHash: id,
        isOnline: data.lastSeen && (Date.now() - data.lastSeen) < ONLINE_TIMEOUT_MS,
        avatar: safeProfilePhoto ? { ...baseAvatar, profilePhoto: safeProfilePhoto } : baseAvatar,
        nickname: data.nickname || `User_${id.slice(-4)}`,
        badges: parseStringArray(data.badges),
        interests,
        streak: Number(data.streak) || 0,
        level: Number(data.level) || 1,
        experience: Number(data.experience) || 0,
        totalActivityDays: Number(data.totalActivityDays) || 0,
        // Matchmaking fields
        lookingFor: data.lookingFor || undefined,
        matchOnboarded: data.matchOnboarded === true || data.matchOnboarded === 'true',
        age: data.age ? Number(data.age) : undefined,
        bio: data.bio || undefined,
        city: data.city || undefined,
        country: data.country || undefined,
      };

      pendingRef.current[id] = user;
      if (!timerRef.current) {
        timerRef.current = setTimeout(() => {
          timerRef.current = null;
          const batch = pendingRef.current;
          pendingRef.current = {};
          setUsers(prev => Object.assign({}, prev, batch));
        }, 50);
      }
    });

    return () => {
      try { mapNode.off(); } catch {}
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    };
  }, []);

  // Re-filter periodically so online status stays fresh — 60s is enough
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 60000);
    return () => clearInterval(id);
  }, []);

  // Recompute only when `users` dict changes or the tick fires (every 60s).
  // Using useMemo stabilises the array reference so MapView's useMemo deps
  // don't bust every time AppInner re-renders for unrelated reasons.
  return useMemo(
    () => Object.values(users).map(u => ({
      ...u,
      isOnline: u.lastSeen ? (Date.now() - u.lastSeen) < ONLINE_TIMEOUT_MS : false,
    })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [users, tick], // tick from the setTick below — ensures 60s refresh
  );
}
